import type { LayoutProps, SpacingProps } from '@shopify/restyle';
import { createRestyleComponent, layout, spacing, useTheme } from '@shopify/restyle';
import type { SwitchProps as NativeSwitchProps, ViewProps } from 'react-native';
import { Switch as NativeSwitch, Platform, Pressable, View } from 'react-native';
import { useEffect } from 'react';
import Animated, { interpolateColor, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { isMobileNative } from 'app/utils/web-helpers';
import type { Theme } from 'app/shopify';

type RestyleProps = LayoutProps<Theme> & SpacingProps<Theme>;

type SwitchPublicProps = RestyleProps & Omit<NativeSwitchProps, 'trackColor'>;

const SwitchBase = createRestyleComponent<RestyleProps & NativeSwitchProps, Theme>([layout, spacing], NativeSwitch);

const IOSWebSwitchContainer = createRestyleComponent<RestyleProps & ViewProps, Theme>([layout, spacing], View);

function IOSWebSwitch(props: SwitchPublicProps) {
  const { value, onValueChange, onChange: _onChange, disabled, ...containerProps } = props;

  const theme = useTheme<Theme>();
  const animatedValue = useSharedValue(value ? 1 : 0);

  useEffect(() => {
    animatedValue.set(withTiming(value ? 1 : 0, { duration: 200 }));
  }, [value, animatedValue]);

  const handlePress = async () => {
    if (!disabled && onValueChange) {
      await onValueChange(!value);
    }
  };

  const trackStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      animatedValue.get(),
      [0, 1],
      [theme.colors['text.placeholder'], theme.colors['surface.brand.default']],
    ),
  }));
  const thumbStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: 3 + animatedValue.get() * 16 }],
  }));

  return (
    <IOSWebSwitchContainer {...containerProps}>
      <Pressable
        accessibilityRole="switch"
        accessibilityState={{ checked: !!value, disabled: !!disabled }}
        onPress={() => void handlePress()}
        disabled={disabled}
        style={{
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Animated.View
          style={[
            {
              width: 44,
              height: 26,
              borderRadius: 13,
              justifyContent: 'center',
            },
            trackStyle,
          ]}
        >
          <Animated.View
            style={[
              {
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: 'white',
                boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.2)',
              },
              thumbStyle,
            ]}
          />
        </Animated.View>
      </Pressable>
    </IOSWebSwitchContainer>
  );
}

export function Switch(props: SwitchPublicProps) {
  const theme = useTheme<Theme>();

  if (!isMobileNative) {
    return <IOSWebSwitch {...props} />;
  }

  return (
    <SwitchBase
      {...props}
      trackColor={{ true: theme.colors['surface.brand.default'] }}
      thumbColor={Platform.OS === 'android' ? theme.colors['surface.brand.bold'] : undefined}
    />
  );
}
